import { analytics } from '@features/analytics';
import { RateOrPackageDetail } from '@features/packages/pages/Package';
import { StaticRate, getAvailableRate, getStaticRate, ratesKeys } from '@features/rates';
import { shouldNoindexRate } from '@features/rates/helpers/shouldNoindexRate.helper';
import { getRoomSettings, roomSettingsKeys } from '@features/roomSettings';
import { PagePropsWithSSR } from '@features/state';
import { NormalizedStore } from '@store/entity/entity.types';
import { yourJourneySelectors } from '@store/yourJourney/selectors';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { parseDateToString } from '@utils/helpers';
import { NextPage, NextPageContext } from 'next';
import NextSeo from 'next-seo';
import Head from 'next/head';
import { useEffect } from 'react';
import { Router } from 'shared-routing';
import { isNotFoundError } from '@common/helpers/isNotFoundError';
import { redirectToListing } from '@common/helpers/notFoundRedirect';
import { Property } from 'shared-types/Property';
import { Routes } from '@common/constants';
import { useTranslations } from '@lang';

export type SpaStaysDetailPageProps = {
  id: string;
  property: Property;
  language: string;
  rate?: StaticRate;
  url: string;
};

const SpaStaysDetailPage: NextPage<SpaStaysDetailPageProps> = ({
  property,
  language,
  rate,
  url,
}) => {
  const { t } = useTranslations();

  useEffect(() => {
    if (!rate) return;

    analytics.track({
      event: 'view_item',
      params: {
        items: [{ item_category: 'spa-stays', item_id: rate.rateCode, item_name: rate.name ?? '' }],
      },
    });
  }, []);

  // The rate is unknown and the redirect is already in flight. Router.replace
  // does not abort the navigation that is rendering us, so this render happens.
  if (!rate) {
    return null;
  }

  const rateTitle = rate.name ?? '';
  const { propertyTitle } = property;

  const pageTitle = `${rateTitle} ${propertyTitle ? `| ${propertyTitle}` : ''}`;

  const shouldNoindex = shouldNoindexRate(rate);

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <NextSeo
        config={{
          noindex: shouldNoindex,
          openGraph: {
            title: pageTitle,
            description: rate.description ?? '',
            locale: language,
            url,
            images: [{ url: rate.images?.[0] ?? '' }],
          },
        }}
      />
      <RateOrPackageDetail
        property={property}
        language={language}
        backRoute={Routes.SPA_STAYS_LIST}
        backText={t('app.backToSpaStays')}
      />
    </>
  );
};

type SpaStaysDetailInitialProps = {
  store: NormalizedStore;
} & NextPageContext &
  Pick<SpaStaysDetailPageProps, 'language' | 'url' | 'property'>;

SpaStaysDetailPage.getInitialProps = async (
  props: SpaStaysDetailInitialProps
): Promise<PagePropsWithSSR<SpaStaysDetailPageProps>> => {
  const { store, property, language, url, res, query } = props;
  const { slug: siteSlug, id: siteId } = property;

  if (!property.modulesExcludingDisabled?.spa?.features?.stays?.enabled) {
    if (res) {
      res.writeHead(302, {
        Location: '/',
      });
      res.end();
    } else {
      Router.push('/');
    }

    // Return, or the not-found redirect below supersedes this one and lands the
    // guest on a listing for a module that is switched off.
    return { id: query.id as string, property, language, url };
  }

  const state = store.getState();
  const journeyParams = yourJourneySelectors.getParsedJourneyParams(state);

  const adults = journeyParams.adults;
  const children = journeyParams.children;
  const childAges = journeyParams.childAges;
  const fromDate = parseDateToString(journeyParams.fromDate);
  const toDate = parseDateToString(journeyParams.toDate);
  const promoRateCode = typeof query.promoRateCode === 'string' ? query.promoRateCode : undefined;
  const rooms = journeyParams.rooms || [{ roomNumber: 1, adults, children }];

  const rateCode = query.id as string;
  const client = new QueryClient();

  let rate;
  try {
    ({ data: rate } = await client.fetchQuery({
      queryKey: ratesKeys.staticRate({ siteSlug, rateCode, language }),
      queryFn: () => getStaticRate({ siteSlug, rateCode, language }),
      // No retries: this is the redirect decision, on the critical path of a
      // response the guest is waiting for.
      retry: false,
    }));
  } catch (error) {
    // Only an unknown rate belongs on the listing. Anything else stays an error.
    if (!isNotFoundError(error)) throw error;
    redirectToListing(res, Routes.SPA_STAYS_LIST, query);
    return { id: rateCode, property, language, url };
  }
  await client.prefetchQuery({
    queryKey: ratesKeys.availableRate(
      rateCode,
      {
        siteId,
        adults,
        children,
        childAges,
        fromDate,
        toDate,
        promoRateCode,
        rooms,
      },
      rooms.length > 1
    ),
    queryFn: () =>
      getAvailableRate(
        rateCode,
        {
          siteId,
          adults,
          children,
          childAges,
          fromDate,
          toDate,
          promoRateCode,
          rooms,
        },
        rooms.length > 1
      ),
  });
  await client.prefetchQuery({
    queryKey: roomSettingsKeys.roomSettings({ siteSlug }),
    queryFn: () => getRoomSettings({ siteSlug }),
  });

  return {
    id: rateCode,
    property,
    language,
    rate,
    url,
    dehydratedState: dehydrate(client),
  };
};

export default SpaStaysDetailPage;
